import React from 'react';
import '../styles/sdg-details.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSeedling,
  faCircleCheck,
  faInfoCircle,
  faTasks,
  faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';
import { SdgHeader } from '../../types/Sdg';
import { Link } from 'react-router-dom';
import { pageNames } from 'config/pageNames';

interface IProps {
  sdg: number;
  eventId?: string;
  showDescription?: boolean;
  changeSdg: () => void;
  onShowMore: () => void;
  expand?: boolean; // kept for compatibility, unused
  sdgHeader: SdgHeader | null;
  canChange: boolean;
}

const SdgDetails: React.FC<IProps> = ({
  sdg,
  eventId,
  showDescription = true,
  changeSdg,
  onShowMore,
  sdgHeader,
  canChange,
}) => {
  if (!sdgHeader) {
    return null; // or a small placeholder if you prefer
  }

  return (
    <section className="action-header-card sdg-details-card">
      {/* Header */}
      <header className="action-header-top">
        <div>
          <p className="action-header-kicker">Selected SDG</p>
          <h2 className="action-header-title">
            {sdgHeader.title}{' '}
            <span className="action-header-sdg-pill">
              <FontAwesomeIcon icon={faSeedling} className="me-1" />
              {sdgHeader.shortTitle}
            </span>
          </h2>
        </div>

        <div className="action-header-actions">
          {canChange && (
            <button
              type="button"
              className="billing-button billing-button--outline action-header-change-btn"
              onClick={changeSdg}
            >
              <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
              Change focus…
            </button>
          )}
        </div>
      </header>

      {/* Body */}
      <div className="action-header-body">
        {showDescription ? (
          <>
            {/* Intro line */}
            <div className="action-header-section">
              <div className="action-header-section-label">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                <span>How this platform supports this SDG</span>
              </div>
              <p className="action-header-goal">
                This platform will help you generate a list of actions to address
                the goal below in a structured and transparent way.
              </p>
            </div>

            {/* Goal description */}
            <div className="action-header-section">
              <div className="action-header-section-label">
                <FontAwesomeIcon icon={faSeedling} className="me-2" />
                <span>Goal description</span>
              </div>
              <p className="action-header-goal">
                {sdgHeader.goal}
              </p>
            </div>

            {/* Indicators list */}
            <div className="action-header-section">
              <div className="action-header-section-label">
                <FontAwesomeIcon icon={faSeedling} className="me-2" />
                <span>Key indicators you can influence</span>
              </div>
              <div className="action-header-indicators">
                {sdgHeader.indicators.map((indicator) => (
                  <div
                    className="action-header-indicator-row"
                    key={indicator.index}
                  >
                    <div className="action-header-indicator-content">
                      <span className="action-header-indicator-icon">
                        <FontAwesomeIcon icon={faSeedling} />
                      </span>
                      <span className="ms-2 action-header-indicator-index">
                        {indicator.index}
                      </span>
                      <span className="ms-2 action-header-indicator-title">
                        {indicator.title}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits block */}
            <div className="action-header-benefits">
              <p className="action-header-section-label">
                Why working on this SDG matters
              </p>
              <ul className="action-header-benefits-list">
                <li>
                  <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
                  <span>Advance the Paris Agreement.</span>
                </li>
                <li>
                  <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
                  <span>Support Race to Zero commitments and climate leadership.</span>
                </li>
              </ul>
            </div>
          </>
        ) : (
          /* Compact mode – CTA only */
          <div className="sdg-details-compact">
            <p className="sdg-details-compact-text">
              View detailed guidance for SDG {sdg} and see which indicators and
              benefits are most relevant for your event.
            </p>
            <div className="sdg-details-compact-actions">
              <button
                type="button"
                className="billing-button billing-button--outline"
                onClick={onShowMore}
              >
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                View SDG details
              </button>

              {eventId && (
                <Link
                  to={`${pageNames.ACTIONS_BOARD}?eventId=${eventId}`}
                  className="billing-button billing-button--primary sdg-details-tasks-btn"
                >
                  <FontAwesomeIcon icon={faTasks} className="me-2" />
                  Work on the tasks
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SdgDetails;
